import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Bike, Car, Trash2, Plus, Lock, Download } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import useFeatureAccess from "@/hooks/useFeatureAccess";

export const VehicleSettingsManagement = () => {
  const { user } = useAuth();
  const { canSaveFeature } = useFeatureAccess();
  const { canSave, reason } = canSaveFeature("vehicle_settings");
  const adminId = user?.id;
  
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSubtype, setShowAddSubtype] = useState(null);
  const [newSubtype, setNewSubtype] = useState({ name: "", description: "", fare_multiplier: 1.0 });
  const [editSubtype, setEditSubtype] = useState(null);
  const [editVehicle, setEditVehicle] = useState(null);
  const [inheriting, setInheriting] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      // Pass admin_id to get admin-specific settings
      const res = await axios.get(`${API}/settings/vehicle-settings?admin_id=${adminId}`);
      // Normalize field names from backend to frontend
      const normalizedSettings = res.data.map(vs => ({
        ...vs,
        // Map per_km_fare to per_km_rate for UI
        per_km_rate: vs.per_km_rate ?? vs.per_km_fare ?? 0,
        // Map minimum_fare to min_fare for UI
        min_fare: vs.min_fare ?? vs.minimum_fare ?? 0,
        per_minute_rate: vs.per_minute_rate ?? 0,
        max_pickup_distance_km: vs.max_pickup_distance_km ?? 10,
        max_ride_distance_km: vs.max_ride_distance_km ?? 50,
        // Normalize subtypes
        subtypes: (vs.subtypes || []).map(st => ({
          ...st,
          fare_multiplier: st.fare_multiplier ?? st.multiplier ?? 1.0
        }))
      }));
      setSettings(normalizedSettings);
    } catch (err) {
      toast.error("Failed to load vehicle settings");
    } finally {
      setLoading(false);
    }
  };

  const checkAndSave = (saveFn) => {
    if (!canSave) {
      toast.error(reason || "This feature is not available");
      return;
    }
    saveFn();
  };

  const handleSaveVehicleSettings = async () => {
    if (!canSave) {
      toast.error(reason || "This feature is not available");
      return;
    }
    if (!editVehicle) return;
    try {
      await axios.put(`${API}/settings/admin/vehicle-settings/${editVehicle.vehicle_type}`, {
        vehicle_type: editVehicle.vehicle_type,
        display_name: editVehicle.display_name,
        base_fare: editVehicle.base_fare,
        per_km_rate: editVehicle.per_km_rate,
        per_minute_rate: editVehicle.per_minute_rate,
        min_fare: editVehicle.min_fare,
        max_pickup_distance_km: editVehicle.max_pickup_distance_km,
        max_ride_distance_km: editVehicle.max_ride_distance_km,
        is_active: editVehicle.is_active,
        subtypes: editVehicle.subtypes,
        // Booking type toggles
        one_way_enabled: editVehicle.one_way_enabled,
        two_way_enabled: editVehicle.two_way_enabled,
        hourly_enabled: editVehicle.hourly_enabled,
        daily_enabled: editVehicle.daily_enabled,
        package_enabled: editVehicle.package_enabled,
        // Two-way pricing
        waiting_charge_per_minute: editVehicle.waiting_charge_per_minute,
        // Hourly pricing
        hourly_rate: editVehicle.hourly_rate,
        hourly_kms_cap: editVehicle.hourly_kms_cap,
        hourly_extra_km_charge: editVehicle.hourly_extra_km_charge,
        hourly_min_hours: editVehicle.hourly_min_hours,
        hourly_max_hours: editVehicle.hourly_max_hours,
        // Daily pricing
        daily_rate: editVehicle.daily_rate,
        daily_max_kms: editVehicle.daily_max_kms,
        daily_extra_km_charge: editVehicle.daily_extra_km_charge,
        daily_min_days: editVehicle.daily_min_days,
        daily_max_days: editVehicle.daily_max_days,
        // Package delivery pricing
        package_base_fare: editVehicle.package_base_fare,
        package_per_km_rate: editVehicle.package_per_km_rate,
        package_max_weight_kg: editVehicle.package_max_weight_kg,
        package_extra_weight_charge: editVehicle.package_extra_weight_charge
      });
      toast.success(`${editVehicle.display_name || editVehicle.vehicle_type} settings updated`);
      setEditVehicle(null);
      fetchSettings();
    } catch (err) {
      toast.error("Failed to update vehicle settings");
    }
  };

  const handleAddSubtype = async (vehicleType) => {
    if (!canSave) {
      toast.error(reason || "This feature is not available");
      return;
    }
    if (!newSubtype.name) {
      toast.error("Please enter a subtype name");
      return;
    }
    try {
      const res = await axios.post(`${API}/settings/admin/vehicle-settings/${vehicleType}/subtypes?admin_id=${user.id}`, {
        name: newSubtype.name,
        description: newSubtype.description,
        multiplier: newSubtype.fare_multiplier  // Backend expects 'multiplier'
      });
      const propagated = res.data?.propagated_to_cities || 0;
      toast.success(`${newSubtype.name} added to ${vehicleType}${propagated > 0 ? ` (copied to ${propagated} cities)` : ''}`);
      setShowAddSubtype(null);
      setNewSubtype({ name: "", description: "", fare_multiplier: 1.0 });
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add subtype");
    }
  };

  const handleUpdateSubtype = async () => {
    if (!canSave) {
      toast.error(reason || "This feature is not available");
      return;
    }
    if (!editSubtype) return;
    try {
      await axios.put(
        `${API}/settings/admin/vehicle-settings/${editSubtype.vehicleType}/subtypes/${editSubtype.subtype.id}`,
        {
          name: editSubtype.subtype.name,
          description: editSubtype.subtype.description,
          multiplier: editSubtype.subtype.fare_multiplier,  // Backend expects 'multiplier'
          is_active: editSubtype.subtype.is_active
        }
      );
      toast.success("Subtype updated");
      setEditSubtype(null);
      fetchSettings();
    } catch (err) {
      toast.error("Failed to update subtype");
    }
  };

  const handleDeleteSubtype = async (vehicleType, subtypeId) => {
    if (!canSave) {
      toast.error(reason || "This feature is not available");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this subtype?")) return;
    try {
      await axios.delete(`${API}/settings/admin/vehicle-settings/${vehicleType}/subtypes/${subtypeId}`);
      toast.success("Subtype deleted");
      fetchSettings();
    } catch (err) {
      toast.error("Failed to delete subtype");
    }
  };

  const handleToggleSubtype = async (vehicleType, subtype) => {
    if (!canSave) {
      toast.error(reason || "This feature is not available");
      return;
    }
    try {
      await axios.put(
        `${API}/settings/admin/vehicle-settings/${vehicleType}/subtypes/${subtype.id}`,
        { ...subtype, is_active: !subtype.is_active }
      );
      toast.success(`${subtype.name} ${!subtype.is_active ? 'enabled' : 'disabled'}`);
      fetchSettings();
    } catch (err) {
      toast.error("Failed to update subtype");
    }
  };

  const handleToggleVehicleType = async (vehicleType, currentStatus) => {
    try {
      await axios.put(
        `${API}/settings/admin/vehicle-settings/${vehicleType}`,
        { is_active: !currentStatus }
      );
      toast.success(`${vehicleType} ${!currentStatus ? 'enabled' : 'disabled'}`);
      fetchSettings();
    } catch (err) {
      toast.error("Failed to update vehicle type");
    }
  };

  if (loading) return <div className="flex justify-center py-8"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const vehicleIcons = { bike: Bike, auto: Car, car: Car };
  const vehicleColors = { bike: "bg-blue-500", auto: "bg-green-500", car: "bg-purple-500" };

  const inheritFromSuperAdmin = async () => {
    if (!window.confirm("This will copy all pricing settings from Super Admin. Your current settings will be overwritten. Continue?")) {
      return;
    }
    
    setInheriting(true);
    try {
      await axios.post(`${API}/admin/inherit-superadmin-pricing?admin_id=${adminId}`);
      toast.success("Successfully inherited pricing from Super Admin");
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to inherit pricing");
    } finally {
      setInheriting(false);
    }
  };

  const handleCleanupDuplicates = async () => {
    if (!window.confirm("This will remove duplicate vehicle types, keeping only the active one (or the one with subtypes). Continue?")) {
      return;
    }
    
    try {
      const res = await axios.post(`${API}/settings/admin/vehicle-settings/${adminId}/cleanup-duplicates`);
      if (res.data.deleted?.length > 0) {
        toast.success(`Cleaned up ${res.data.deleted.length} duplicate(s)`);
      } else {
        toast.info("No duplicates found");
      }
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cleanup duplicates");
    }
  };

  const handleDeleteVehicleSetting = async (settingId, vehicleType) => {
    if (!window.confirm(`Are you sure you want to delete this ${vehicleType} setting? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/settings/admin/vehicle-settings/${adminId}/${settingId}`);
      toast.success(`${vehicleType} setting deleted`);
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete vehicle setting");
    }
  };

  return (
    <div data-testid="vehicle-settings-management">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold">Vehicle Settings</h2>
          <p className="text-muted-foreground">Configure vehicle types, subtypes, and their pricing</p>
        </div>
        <div className="flex gap-2">
          {canSave && (
            <>
              <Button 
                variant="outline" 
                onClick={handleCleanupDuplicates}
                className="gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Cleanup Duplicates
              </Button>
              <Button 
                variant="outline" 
                onClick={inheritFromSuperAdmin}
                disabled={inheriting}
                className="gap-2"
              >
                <Download className="w-4 h-4" />
                {inheriting ? "Inheriting..." : "Inherit from Super Admin"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Restriction Banner */}
      {!canSave && reason && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
          <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div>
            <p className="font-medium text-amber-800">{reason}</p>
            <p className="text-sm text-amber-600">You can view settings but cannot make changes.</p>
          </div>
        </div>
      )}

      <div className="grid gap-6">
        {settings.map((vs) => {
          const Icon = vehicleIcons[vs.vehicle_type] || Car;

          return (
            <div key={vs.id || vs.vehicle_type} className="bg-card rounded-xl border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 ${vehicleColors[vs.vehicle_type]} rounded-lg flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold capitalize">{vs.display_name || vs.vehicle_type}</h3>
                    <p className="text-sm text-muted-foreground">
                      {vs.is_active ? 'Active' : 'Inactive'} • {vs.subtypes?.length || 0} subtypes
                      {vs.id && <span className="text-xs ml-2 text-gray-400">ID: {vs.id.slice(-6)}</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditVehicle({ ...vs })}>
                    Edit Settings
                  </Button>
                  {/* Delete button - always visible */}
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => handleDeleteVehicleSetting(vs.id || vs.vehicle_type, vs.vehicle_type)}
                    title={`Delete this ${vs.vehicle_type} setting`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <Switch 
                    checked={vs.is_active} 
                    onCheckedChange={() => handleToggleVehicleType(vs.vehicle_type, vs.is_active)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-4">
                <div>
                  <p className="text-xs text-muted-foreground">Base Fare</p>
                  <p className="font-semibold">₹{vs.base_fare ?? 0}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Per KM Rate</p>
                  <p className="font-semibold">₹{vs.per_km_rate ?? vs.per_km_fare ?? 0}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Per Min Rate</p>
                  <p className="font-semibold">₹{vs.per_minute_rate ?? 0}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Min Fare</p>
                  <p className="font-semibold">₹{vs.min_fare ?? vs.minimum_fare ?? 0}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Max Pickup</p>
                  <p className="font-semibold">{vs.max_pickup_distance_km ?? 0} km</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Max Ride</p>
                  <p className="font-semibold">{vs.max_ride_distance_km ?? 0} km</p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium">Subtypes & Pricing</p>
                  <Button size="sm" variant="outline" onClick={() => setShowAddSubtype(vs.vehicle_type)}>
                    <Plus className="w-4 h-4 mr-1" /> Add Subtype
                  </Button>
                </div>
                
                {vs.subtypes && vs.subtypes.length > 0 ? (
                  <div className="grid gap-2">
                    {vs.subtypes.map((subtype) => (
                      <div key={subtype.id} className={`flex items-center justify-between p-3 rounded-lg ${subtype.is_active ? 'bg-muted' : 'bg-muted/50 opacity-60'}`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{subtype.name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${subtype.fare_multiplier === 1 ? 'bg-gray-200 text-gray-700' : subtype.fare_multiplier < 1 ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                              {subtype.fare_multiplier}x fare
                            </span>
                            {!subtype.is_active && <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">Disabled</span>}
                          </div>
                          <p className="text-xs text-muted-foreground">{subtype.description}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={subtype.is_active}
                            onCheckedChange={() => handleToggleSubtype(vs.vehicle_type, subtype)}
                          />
                          <Button size="sm" variant="ghost" onClick={() => setEditSubtype({ vehicleType: vs.vehicle_type, subtype: { ...subtype } })}>
                            Edit
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteSubtype(vs.vehicle_type, subtype.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No subtypes configured. Add subtypes to offer different ride options.</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Subtype Dialog */}
      <Dialog open={!!showAddSubtype} onOpenChange={() => setShowAddSubtype(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Subtype to {showAddSubtype?.charAt(0).toUpperCase() + showAddSubtype?.slice(1)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input 
                value={newSubtype.name} 
                onChange={(e) => setNewSubtype({ ...newSubtype, name: e.target.value })} 
                placeholder="e.g., Scooter, Magic Auto, Luxury"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input 
                value={newSubtype.description} 
                onChange={(e) => setNewSubtype({ ...newSubtype, description: e.target.value })} 
                placeholder="e.g., Quick & affordable"
              />
            </div>
            <div>
              <Label>Fare Multiplier</Label>
              <div className="flex items-center gap-2">
                <Input 
                  type="number" 
                  step="0.1"
                  min="0.1"
                  max="5"
                  value={newSubtype.fare_multiplier} 
                  onChange={(e) => setNewSubtype({ ...newSubtype, fare_multiplier: parseFloat(e.target.value) })} 
                />
                <span className="text-sm text-muted-foreground">x base fare</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">1.0 = base fare, 0.7 = 30% cheaper, 1.5 = 50% more expensive</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSubtype(null)}>Cancel</Button>
            <Button onClick={() => handleAddSubtype(showAddSubtype)}>Add Subtype</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Subtype Dialog */}
      <Dialog open={!!editSubtype} onOpenChange={() => setEditSubtype(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit {editSubtype?.subtype?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input 
                value={editSubtype?.subtype?.name || ""} 
                onChange={(e) => setEditSubtype({ ...editSubtype, subtype: { ...editSubtype.subtype, name: e.target.value } })} 
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input 
                value={editSubtype?.subtype?.description || ""} 
                onChange={(e) => setEditSubtype({ ...editSubtype, subtype: { ...editSubtype.subtype, description: e.target.value } })} 
              />
            </div>
            <div>
              <Label>Fare Multiplier</Label>
              <div className="flex items-center gap-2">
                <Input 
                  type="number" 
                  step="0.1"
                  min="0.1"
                  max="5"
                  value={editSubtype?.subtype?.fare_multiplier || 1.0} 
                  onChange={(e) => setEditSubtype({ ...editSubtype, subtype: { ...editSubtype.subtype, fare_multiplier: parseFloat(e.target.value) } })} 
                />
                <span className="text-sm text-muted-foreground">x base fare</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSubtype(null)}>Cancel</Button>
            <Button onClick={handleUpdateSubtype}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Vehicle Settings Dialog */}
      <Dialog open={!!editVehicle} onOpenChange={() => setEditVehicle(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Edit {editVehicle?.display_name || editVehicle?.vehicle_type} Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Display Name</Label>
                <Input 
                  value={editVehicle?.display_name || ""} 
                  onChange={(e) => setEditVehicle({ ...editVehicle, display_name: e.target.value })} 
                  placeholder="e.g., Bike, Auto, Car"
                />
              </div>
              <div>
                <Label>Status</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Switch 
                    checked={editVehicle?.is_active !== false} 
                    onCheckedChange={(checked) => setEditVehicle({ ...editVehicle, is_active: checked })}
                  />
                  <span>{editVehicle?.is_active !== false ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
            </div>
            
            {/* Booking Types Available */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                Available Booking Types
              </h4>
              <p className="text-sm text-muted-foreground mb-3">Turn ON/OFF different booking types for this vehicle</p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <span className="text-sm font-medium">One Way</span>
                  <Switch 
                    checked={editVehicle?.one_way_enabled !== false} 
                    onCheckedChange={(checked) => setEditVehicle({ ...editVehicle, one_way_enabled: checked })}
                  />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <span className="text-sm font-medium">Two Way</span>
                  <Switch 
                    checked={editVehicle?.two_way_enabled !== false} 
                    onCheckedChange={(checked) => setEditVehicle({ ...editVehicle, two_way_enabled: checked })}
                  />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <span className="text-sm font-medium">Hourly</span>
                  <Switch 
                    checked={editVehicle?.hourly_enabled !== false} 
                    onCheckedChange={(checked) => setEditVehicle({ ...editVehicle, hourly_enabled: checked })}
                  />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <span className="text-sm font-medium">Daily</span>
                  <Switch 
                    checked={editVehicle?.daily_enabled !== false} 
                    onCheckedChange={(checked) => setEditVehicle({ ...editVehicle, daily_enabled: checked })}
                  />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <span className="text-sm font-medium">Package</span>
                  <Switch 
                    checked={editVehicle?.package_enabled !== false} 
                    onCheckedChange={(checked) => setEditVehicle({ ...editVehicle, package_enabled: checked })}
                  />
                </div>
              </div>
            </div>
            
            {/* One-Way / Base Pricing */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                One-Way Pricing (Base)
                {editVehicle?.one_way_enabled === false && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Disabled</span>}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>Base Fare (₹)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="1"
                    value={editVehicle?.base_fare || 0} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, base_fare: parseFloat(e.target.value) })} 
                  />
                </div>
                <div>
                  <Label>Per KM Rate (₹)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="0.5"
                    value={editVehicle?.per_km_rate || 0} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, per_km_rate: parseFloat(e.target.value) })} 
                  />
                </div>
                <div>
                  <Label>Per Minute Rate (₹)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="0.5"
                    value={editVehicle?.per_minute_rate || 0} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, per_minute_rate: parseFloat(e.target.value) })} 
                  />
                </div>
                <div>
                  <Label>Minimum Fare (₹)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="1"
                    value={editVehicle?.min_fare || 0} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, min_fare: parseFloat(e.target.value) })} 
                  />
                </div>
              </div>
            </div>

            {/* Two-Way Pricing */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                Two-Way / Round Trip Pricing
                {editVehicle?.two_way_enabled === false && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Disabled</span>}
              </h4>
              <p className="text-xs text-muted-foreground mb-3">Uses base pricing for fare + waiting charges per minute</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Waiting Charge (₹/min)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="0.5"
                    value={editVehicle?.waiting_charge_per_minute || 2} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, waiting_charge_per_minute: parseFloat(e.target.value) })} 
                    disabled={editVehicle?.two_way_enabled === false}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Charged while driver waits at destination</p>
                </div>
              </div>
            </div>

            {/* Hourly Booking Pricing */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                Hourly Booking Pricing
                {editVehicle?.hourly_enabled === false && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Disabled</span>}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label>Hourly Rate (₹/hr)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="10"
                    value={editVehicle?.hourly_rate || 100} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, hourly_rate: parseFloat(e.target.value) })} 
                  />
                </div>
                <div>
                  <Label>KMs Cap Per Hour</Label>
                  <Input 
                    type="number"
                    min="1"
                    step="1"
                    value={editVehicle?.hourly_kms_cap || 10} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, hourly_kms_cap: parseFloat(e.target.value) })} 
                  />
                  <p className="text-xs text-muted-foreground mt-1">KMs included per hour</p>
                </div>
                <div>
                  <Label>Extra KM Charge (₹)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="1"
                    value={editVehicle?.hourly_extra_km_charge || 12} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, hourly_extra_km_charge: parseFloat(e.target.value) })} 
                  />
                </div>
                <div>
                  <Label>Min Hours</Label>
                  <Input 
                    type="number"
                    min="1"
                    max="24"
                    step="1"
                    value={editVehicle?.hourly_min_hours || 1} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, hourly_min_hours: parseInt(e.target.value) })} 
                  />
                </div>
                <div>
                  <Label>Max Hours/Day</Label>
                  <Input 
                    type="number"
                    min="1"
                    max="24"
                    step="1"
                    value={editVehicle?.hourly_max_hours || 12} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, hourly_max_hours: parseInt(e.target.value) })} 
                  />
                </div>
              </div>
            </div>

            {/* Daily Booking Pricing */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                Daily Booking Pricing
                {editVehicle?.daily_enabled === false && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Disabled</span>}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label>Daily Rate (₹/day)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="100"
                    value={editVehicle?.daily_rate || 1500} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, daily_rate: parseFloat(e.target.value) })} 
                    disabled={editVehicle?.daily_enabled === false}
                  />
                </div>
                <div>
                  <Label>Max KMs Per Day</Label>
                  <Input 
                    type="number"
                    min="10"
                    step="10"
                    value={editVehicle?.daily_max_kms || 150} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, daily_max_kms: parseFloat(e.target.value) })} 
                    disabled={editVehicle?.daily_enabled === false}
                  />
                  <p className="text-xs text-muted-foreground mt-1">KMs included per day</p>
                </div>
                <div>
                  <Label>Extra KM Charge (₹)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="1"
                    value={editVehicle?.daily_extra_km_charge || 10} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, daily_extra_km_charge: parseFloat(e.target.value) })} 
                    disabled={editVehicle?.daily_enabled === false}
                  />
                </div>
                <div>
                  <Label>Min Days</Label>
                  <Input 
                    type="number"
                    min="1"
                    max="30"
                    step="1"
                    value={editVehicle?.daily_min_days || 1} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, daily_min_days: parseInt(e.target.value) })} 
                    disabled={editVehicle?.daily_enabled === false}
                  />
                </div>
                <div>
                  <Label>Max Days</Label>
                  <Input 
                    type="number"
                    min="1"
                    max="90"
                    step="1"
                    value={editVehicle?.daily_max_days || 30} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, daily_max_days: parseInt(e.target.value) })} 
                    disabled={editVehicle?.daily_enabled === false}
                  />
                </div>
              </div>
            </div>

            {/* Package Delivery Pricing */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-teal-500"></span>
                Package Delivery Pricing
                {editVehicle?.package_enabled === false && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Disabled</span>}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>Base Fare (₹)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="5"
                    value={editVehicle?.package_base_fare || 30} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, package_base_fare: parseFloat(e.target.value) })} 
                    disabled={editVehicle?.package_enabled === false}
                  />
                </div>
                <div>
                  <Label>Per KM Rate (₹)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="1"
                    value={editVehicle?.package_per_km_rate || 8} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, package_per_km_rate: parseFloat(e.target.value) })} 
                  />
                </div>
                <div>
                  <Label>Max Weight (kg)</Label>
                  <Input 
                    type="number"
                    min="1"
                    max="100"
                    step="1"
                    value={editVehicle?.package_max_weight_kg || 10} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, package_max_weight_kg: parseFloat(e.target.value) })} 
                  />
                  <p className="text-xs text-muted-foreground mt-1">Weight included in base fare</p>
                </div>
                <div>
                  <Label>Extra Weight (₹/kg)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="1"
                    value={editVehicle?.package_extra_weight_charge || 5} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, package_extra_weight_charge: parseFloat(e.target.value) })} 
                  />
                  <p className="text-xs text-muted-foreground mt-1">Charge per kg over limit</p>
                </div>
              </div>
            </div>
            
            {/* Distance Limits */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Distance Limits</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Max Pickup Distance (km)</Label>
                  <Input 
                    type="number"
                    min="1"
                    max="50"
                    step="1"
                    value={editVehicle?.max_pickup_distance_km || 5} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, max_pickup_distance_km: parseFloat(e.target.value) })} 
                  />
                  <p className="text-xs text-muted-foreground mt-1">How far a driver can be from pickup</p>
                </div>
                <div>
                  <Label>Max Ride Distance (km)</Label>
                  <Input 
                    type="number"
                    min="1"
                    max="500"
                    step="5"
                    value={editVehicle?.max_ride_distance_km || 50} 
                    onChange={(e) => setEditVehicle({ ...editVehicle, max_ride_distance_km: parseFloat(e.target.value) })} 
                  />
                  <p className="text-xs text-muted-foreground mt-1">Max distance customer can travel</p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditVehicle(null)}>Cancel</Button>
            <Button onClick={handleSaveVehicleSettings}>Save Settings</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VehicleSettingsManagement;
